import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Mic, MicOff, Check, AlertTriangle, Loader2, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MainLayout } from "@/components/layout/MainLayout";
import { useTranslation } from "@/hooks/useTranslation";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";

const SENTENCES = [
  "שלום, אני מקליט את הקול שלי כדי ליצור קול מותאם אישית.",
  "היום מזג האוויר נעים מאוד והשמש זורחת בחוץ.",
  "אני אוהב לשמוע מוזיקה ולקרוא ספרים בזמן הפנוי שלי.",
  "המשפחה שלי חשובה לי מאוד ואני נהנה לבלות איתם.",
  "תודה רבה על ההקשבה, אני מקווה שהקול שלי יישמע טוב.",
];

type Stage = "consent" | "recording" | "processing" | "success" | "error";

export default function VoiceCloning() {
  const navigate = useNavigate();
  const { dir } = useTranslation();
  const isHebrew = dir === "rtl";
  
  const [stage, setStage] = useState<Stage>("consent");
  const [currentSentence, setCurrentSentence] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState<Blob[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const handleBack = () => {
    stopRecording();
    navigate("/settings");
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordings(prev => [...prev, blob]);
        
        if (currentSentence < SENTENCES.length - 1) {
          setCurrentSentence(prev => prev + 1);
        } else {
          setStage("processing");
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast.error(isHebrew ? "לא ניתן לגשת למיקרופון" : "Cannot access microphone");
    }
  };

  useEffect(() => {
    if (stage === "processing" && recordings.length === SENTENCES.length) {
      cloneVoice();
    }
  }, [stage, recordings]);

  const cloneVoice = async () => {
    try {
      const audioBase64Array: string[] = [];
      
      for (const blob of recordings) {
        const buffer = await blob.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
        );
        audioBase64Array.push(base64);
      }

      const { data, error } = await supabase.functions.invoke("clone-voice", {
        body: { audioSamples: audioBase64Array },
      });

      if (error) throw error;
      
      if (data?.voiceId) {
        // Save the cloned voice ID to settings
        const settings = getUserSettings();
        saveUserSettings({ ...settings, clonedVoiceId: data.voiceId });
        
        setStage("success");
        toast.success(isHebrew ? "הקול שלך שוכפל בהצלחה!" : "Voice cloned successfully!");
      } else {
        throw new Error(data?.error || "Failed to clone voice");
      }
    } catch (error: any) {
      setErrorMessage(error.message || (isHebrew ? "שגיאה בשכפול הקול" : "Error cloning voice"));
      setStage("error");
    }
  };

  const handleConsent = () => {
    setStage("recording");
  };

  const handleRetry = () => {
    setStage("consent");
    setCurrentSentence(0);
    setRecordings([]);
    setErrorMessage("");
  };

  const progress = (currentSentence / SENTENCES.length) * 100;

  return (
    <MainLayout onBack={handleBack}>
      <div className="flex-1 flex flex-col p-6 overflow-auto" dir={dir}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">
            {isHebrew ? "שכפול קול" : "Voice Cloning"}
          </h1>
        </div>

        <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col">
          {stage === "consent" && (
            <div className="space-y-6">
              <div className="bg-amber-500/10 p-6 rounded-2xl border border-amber-500/20">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-8 h-8 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="space-y-3">
                    <p className="font-bold text-xl">
                      {isHebrew ? "הסכמה לשכפול קול" : "Voice Cloning Consent"}
                    </p>
                    <p className="text-muted-foreground text-lg">
                      {isHebrew ? 'בלחיצה על "אני מסכים/ה" אתה מאשר/ת ש:' : 'By clicking "I Agree" you confirm that:'}
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 mr-2 text-lg">
                      <li>{isHebrew ? "הקול שתקליט/י הוא הקול שלך" : "The voice you record is your own"}</li>
                      <li>{isHebrew ? "אתה מסכים/ה שהקול ישמש באפליקציה זו" : "You agree the voice will be used in this app"}</li>
                      <li>{isHebrew ? "הקלטות ישלחו לשירות ElevenLabs לעיבוד" : "Recordings will be sent to ElevenLabs for processing"}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-muted/50 p-6 rounded-2xl">
                <p className="text-lg text-muted-foreground mb-4">
                  {isHebrew ? "תקליט/י 5 משפטים:" : "You'll record 5 sentences:"}
                </p>
                <ol className="space-y-2 list-decimal list-inside">
                  {SENTENCES.map((s, i) => (
                    <li key={i} className="text-muted-foreground text-lg">{s}</li>
                  ))}
                </ol>
              </div>

              <div className="flex gap-4 pt-4">
                <Button variant="outline" onClick={handleBack} className="flex-1 h-16 text-xl">
                  {isHebrew ? "ביטול" : "Cancel"}
                </Button>
                <Button onClick={handleConsent} className="flex-1 h-16 text-xl">
                  {isHebrew ? "אני מסכים/ה" : "I Agree"}
                </Button>
              </div>
            </div>
          )}

          {stage === "recording" && (
            <div className="space-y-8 flex-1 flex flex-col items-center justify-center">
              <div className="text-center w-full">
                <p className="text-lg text-muted-foreground mb-3">
                  {isHebrew ? `משפט ${currentSentence + 1} מתוך ${SENTENCES.length}` : `Sentence ${currentSentence + 1} of ${SENTENCES.length}`}
                </p>
                <Progress value={progress} className="h-3" />
              </div>

              <div className="bg-muted/50 p-10 rounded-2xl text-center">
                <p className="text-2xl font-medium leading-relaxed">
                  "{SENTENCES[currentSentence]}"
                </p>
              </div>

              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={cn(
                  "w-32 h-32 rounded-full flex items-center justify-center transition-all",
                  isRecording 
                    ? "bg-red-500 hover:bg-red-600 animate-pulse" 
                    : "bg-primary hover:bg-primary/90"
                )}
              >
                {isRecording ? (
                  <MicOff className="w-14 h-14 text-white" />
                ) : (
                  <Mic className="w-14 h-14 text-white" />
                )}
              </button>

              <p className="text-center text-lg text-muted-foreground">
                {isRecording 
                  ? (isHebrew ? "לחץ לסיום ההקלטה" : "Click to stop recording") 
                  : (isHebrew ? "לחץ להתחלת הקלטה" : "Click to start recording")}
              </p>
            </div>
          )}

          {stage === "processing" && (
            <div className="flex-1 flex flex-col items-center justify-center space-y-6">
              <Loader2 className="w-20 h-20 text-primary animate-spin" />
              <p className="text-2xl font-medium">
                {isHebrew ? "משכפל את הקול שלך..." : "Cloning your voice..."}
              </p>
              <p className="text-lg text-muted-foreground">
                {isHebrew ? "זה עלול לקחת מספר שניות" : "This may take a few seconds"}
              </p>
            </div>
          )}

          {stage === "success" && (
            <div className="flex-1 flex flex-col items-center justify-center space-y-6">
              <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center">
                <Check className="w-14 h-14 text-green-500" />
              </div>
              <p className="text-2xl font-medium">
                {isHebrew ? "הקול שוכפל בהצלחה!" : "Voice cloned successfully!"}
              </p>
              <p className="text-lg text-muted-foreground text-center">
                {isHebrew 
                  ? 'עכשיו תוכל/י לבחור "הקול שלי" בהגדרות הקול' 
                  : 'You can now select "My Voice" in voice settings'}
              </p>
              <Button onClick={() => navigate("/settings")} className="h-16 px-12 text-xl mt-4">
                {isHebrew ? "חזרה להגדרות" : "Back to Settings"}
              </Button>
            </div>
          )}

          {stage === "error" && (
            <div className="flex-1 flex flex-col items-center justify-center space-y-6">
              <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-14 h-14 text-red-500" />
              </div>
              <p className="text-2xl font-medium">
                {isHebrew ? "שגיאה בשכפול הקול" : "Error cloning voice"}
              </p>
              <p className="text-lg text-muted-foreground text-center">{errorMessage}</p>
              <div className="flex gap-4 mt-4">
                <Button variant="outline" onClick={handleBack} className="h-14 px-8 text-lg">
                  {isHebrew ? "סגור" : "Close"}
                </Button>
                <Button onClick={handleRetry} className="h-14 px-8 text-lg">
                  {isHebrew ? "נסה שוב" : "Try Again"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
